/**
 * LAS REGLAS DEL MOTOR DE PROGRESIÓN — versionadas y congeladas.
 *
 * Todo lo que hay aquí son números cerrados en la cadena de diseño (RESULTADO-6,
 * 7 y 7B). No son constantes de implementación: son **el contrato del producto**.
 * Cambiar uno cambia el pasado de todos los usuarios, y por eso el conjunto lleva
 * versión: un logro sellado con `v1` se recalcula siempre con `v1` (PRD 8.7).
 *
 * La unidad se llama «puntos» en la interfaz. No es moneda: **no se gasta, no se
 * pierde y no se transfiere.** Nada resta nunca.
 */

export const RULESET_VERSION = 5;

// ---------------------------------------------------------------------------
// QUÉ DA PUNTOS
// ---------------------------------------------------------------------------

export const POINTS = {
  /** Hábito marcado como hecho. */
  habitDone: 10,
  /** Extra si el hábito es cuantificable y se pasó del objetivo. */
  habitOverTarget: 5,
  /** Hábito omitido a propósito. Cero, nunca negativo. */
  habitSkipped: 0,
  /** Sesión de foco de 25 minutos. */
  focus25: 15,
  /** Sesión de foco de 50 minutos. */
  focus50: 30,
  /** Sesión interrumpida. Cero: no se castiga, tampoco se premia. */
  focusInterrupted: 0,
  /** Entrada de diario. Va a General. */
  journalEntry: 5,
  /** Ánimo y energía del día. Va a General. */
  moodLog: 3,
  /** Hito de una meta. */
  goalMilestone: 50,
  /** Meta cumplida. */
  goalCompleted: 200,
  /** Revisión semanal, repartida entre las dimensiones activas. */
  weeklyReview: 60,
  /** Revisión mensual, repartida. */
  monthlyReview: 150,
  /** Ritual matutino sellado. Va al pilar del día, o a General. */
  ritualSealed: 15,
  /** «Lo difícil primero» del día, hecho. Una por plan de día. */
  frogDone: 20,
  /** Tarea del planificador hecha. Poco a propósito: premiar listas largas es premiar ruido. */
  taskDone: 2,
  /** Rueda de la vida del mes, sellada. Repartida entre las áreas activas, una por mes (v3). */
  lifeWheel: 40,
  /**
   * Cada área extra que trabaja un hábito hecho (v4): el ejercicio da sus 10 a Cuerpo
   * y 5 más a Mente. La principal no se frena por compartir.
   */
  habitExtraArea: 5,
  /** Sueño cumplido (v5). Va al área del sueño. Sus pasos no puntúan: aclaran su trofeo. */
  dreamCompleted: 300,
} as const;

export type PointKind = keyof typeof POINTS;

/**
 * Máximo de tareas que puntúan por día. Sin esto, la forma óptima de subir sería
 * partir una tarea en veinte; con él, la tarea suma lo justo para notarse.
 */
export const TASKS_PER_DAY_CAP = 5;

/** Cuántas áreas extra puede trabajar un hábito además de la suya (v4). */
export const MAX_AREAS_EXTRA = 2;

/**
 * TOPES DIARIOS. Existen por una razón de producto, no de equilibrio: sin tope,
 * la forma óptima de subir es amontonar registros un domingo, y eso es
 * exactamente lo contrario de lo que la app defiende.
 */
export const DAILY_CAP_PER_DIMENSION = 60;
export const DAILY_CAP_TOTAL = 150;

// ---------------------------------------------------------------------------
// LA CURVA DE NIVEL
// ---------------------------------------------------------------------------

/** Coste de pasar del nivel 10 al 11. Ancla de la segunda mitad de la curva. */
const COST_AT_10 = 600;

/**
 * Coste de subir DEL nivel `n` al `n + 1`, redondeado a múltiplo de 5.
 *
 *   n ≤ 10 → 100 × 1,22^(n−1)
 *   n > 10 → 600 × 1,10^(n−10)
 *
 * El cambio de base en el 10 es deliberado: 1,22 hace que los primeros niveles
 * lleguen rápido —nivel 2 al tercer día— y 1,10 evita que el nivel 30 sea
 * inalcanzable. Acumulado al nivel 10: 2.260 puntos (76 días a 30/día); al
 * nivel 20: 11.825 (395 días).
 *
 * El paso 6 corrigió aquí un error aritmético de mi brief: el coste 8 → 9 es
 * **400**, no 405. La fórmula da 402,27 y el múltiplo de 5 más cercano es 400.
 */
export function levelCost(level: number): number {
  if (level < 1) throw new RangeError(`nivel fuera de rango: ${level}`);
  const raw = level <= 10 ? 100 * 1.22 ** (level - 1) : COST_AT_10 * 1.1 ** (level - 10);
  return Math.round(raw / 5) * 5;
}

/** Techo de niveles que se precalculan. Muy por encima de cualquier uso real. */
const MAX_LEVEL = 120;

/** Acumulado necesario para ALCANZAR cada nivel. `[0, 100, 220, 370, …]`. */
const THRESHOLDS: readonly number[] = (() => {
  const out = [0];
  let total = 0;
  for (let level = 1; level < MAX_LEVEL; level += 1) {
    total += levelCost(level);
    out.push(total);
  }
  return out;
})();

export type LevelState = {
  readonly level: number;
  /** Puntos acumulados dentro del nivel actual. */
  readonly into: number;
  /** Puntos que cuesta el nivel actual entero. */
  readonly span: number;
  /** 0 a 1. Lo que la interfaz dibuja como anillo o barra. */
  readonly progress: number;
  /** Puntos que faltan para el siguiente. */
  readonly toNext: number;
};

export function levelFromPoints(points: number): LevelState {
  const total = Math.max(0, Math.floor(points));

  let level = 1;
  while (level < MAX_LEVEL - 1 && total >= (THRESHOLDS[level] ?? Infinity)) level += 1;

  const floorAt = THRESHOLDS[level - 1] ?? 0;
  const span = levelCost(level);
  const into = total - floorAt;

  return {
    level,
    into,
    span,
    progress: span === 0 ? 0 : Math.min(1, into / span),
    toNext: Math.max(0, span - into),
  };
}

export function pointsForLevel(level: number): number {
  return THRESHOLDS[Math.max(1, Math.min(MAX_LEVEL - 1, level)) - 1] ?? 0;
}

// ---------------------------------------------------------------------------
// LOS CINCO TRAMOS DEL CAMINO — de por vida, se cruzan una vez
// ---------------------------------------------------------------------------

export type Tramo = {
  readonly key: string;
  readonly name: string;
  readonly from: number;
  /** `null` en el último: Cénit no termina. */
  readonly to: number | null;
  /** Índices de hito (1-based) que contiene, cuando el catálogo llega. */
  readonly milestones: readonly [number, number] | null;
};

/**
 * Territorio «el salto»: el cuerpo que sube. Anda, salta, vuela, está en auge,
 * está en lo más alto. El paso 7 lo eligió frente a «el día» porque una
 * trayectoria no tiene tarde — y el camino nunca retrocede.
 *
 * El catálogo de hitos cierra en el 25 (fin de Salto, 10.000). De ahí en
 * adelante el camino enseña **una pieza de horizonte**, no nodos vacíos: 75
 * casillas en blanco eran un muro, no un futuro.
 */
export const TRAMOS: readonly Tramo[] = [
  { key: 'paso', name: 'Paso', from: 0, to: 2_500, milestones: [1, 10] },
  { key: 'salto', name: 'Salto', from: 2_500, to: 10_000, milestones: [11, 25] },
  { key: 'vuelo', name: 'Vuelo', from: 10_000, to: 35_000, milestones: null },
  { key: 'auge', name: 'Auge', from: 35_000, to: 80_000, milestones: null },
  { key: 'cenit', name: 'Cénit', from: 80_000, to: null, milestones: null },
];

export function tramoForPoints(points: number): Tramo {
  // El primero siempre existe: TRAMOS es una constante de cinco elementos y hay
  // un test que lo comprueba. Aun así se escribe sin `!`, para que si alguien
  // vacía la lista el fallo sea un tramo neutro y no un error en tiempo de
  // ejecución en la pantalla principal.
  let current: Tramo = TRAMOS[0] ?? FALLBACK_TRAMO;
  for (const tramo of TRAMOS) if (points >= tramo.from) current = tramo;
  return current;
}

const FALLBACK_TRAMO: Tramo = { key: 'paso', name: 'Paso', from: 0, to: null, milestones: null };

// ---------------------------------------------------------------------------
// EL CATÁLOGO DE HITOS
// ---------------------------------------------------------------------------

export type MilestoneCategory = 'personalizacion' | 'profundidad' | 'reconocimiento';

export type Milestone = {
  readonly n: number;
  readonly key: string;
  readonly points: number;
  readonly name: string;
  readonly category: MilestoneCategory;
  /**
   * Qué significa haber llegado aquí, en una frase. Antes prometía premios (sonidos,
   * fondos, acentos) que la app no tiene; un hito no promete nada que no exista.
   */
  readonly unlocks: string;
};

/**
 * LA REGLA QUE ORDENA ESTE CATÁLOGO: **solo se desbloquea lo que el producto no
 * necesitaba.**
 *
 * El paso 6 había puesto detrás de un hito la exportación CSV (3.500 puntos, 117
 * días) y la del diario (8.500, 283 días). Eso es portabilidad de datos —RGPD
 * art. 20— y no puede depender de una mecánica de juego. El paso 7 lo
 * recatalogó: las dos exportaciones viven en Ajustes → Tus datos **desde el
 * primer día y sin condición**, junto a otras seis funciones de ajuste fino que
 * estaban igual de mal puestas.
 *
 * Y las nueve vistas de Progreso existen desde el minuto uno: dicen «necesitas
 * más registros · faltan N». El hito no revela la vista, **la deja llena**.
 */
export const MILESTONES: readonly Milestone[] = [
  {
    n: 1,
    key: 'arranque',
    points: 250,
    name: 'Arranque',
    category: 'reconocimiento',
    unlocks: 'Tu primer hito: desde aquí se guardan en Perfil',
  },
  {
    n: 2,
    key: 'impulso',
    points: 500,
    name: 'Impulso',
    category: 'personalizacion',
    unlocks: 'Lo que haces empieza a notarse',
  },
  {
    n: 3,
    key: 'zancada',
    points: 750,
    name: 'Zancada',
    category: 'profundidad',
    unlocks: 'Avanzas a buen paso, sin prisa',
  },
  {
    n: 4,
    key: 'ritmo',
    points: 1_000,
    name: 'Ritmo',
    category: 'personalizacion',
    unlocks: 'Mil puntos: ya tienes un ritmo propio',
  },
  {
    n: 5,
    key: 'marcha',
    points: 1_250,
    name: 'Marcha',
    category: 'personalizacion',
    unlocks: 'Esto ya no es un intento: es costumbre',
  },
  {
    n: 6,
    key: 'tranco',
    points: 1_500,
    name: 'Tranco',
    category: 'profundidad',
    unlocks: 'Pasos largos y seguidos',
  },
  {
    n: 7,
    key: 'compas',
    points: 1_750,
    name: 'Compás',
    category: 'personalizacion',
    unlocks: 'Tus días ya siguen un compás',
  },
  {
    n: 8,
    key: 'empuje',
    points: 2_000,
    name: 'Empuje',
    category: 'profundidad',
    unlocks: 'Dos mil puntos de evidencia de quién eres',
  },
  {
    n: 9,
    key: 'inercia',
    points: 2_250,
    name: 'Inercia',
    category: 'personalizacion',
    unlocks: 'Seguir ya te cuesta menos que parar',
  },
  {
    n: 10,
    key: 'despegue',
    points: 2_500,
    name: 'Despegue',
    category: 'reconocimiento',
    unlocks: 'Cierras el tramo Paso: la base está hecha',
  },
  {
    n: 11,
    key: 'brinco',
    points: 3_000,
    name: 'Brinco',
    category: 'personalizacion',
    unlocks: 'Empiezas a subir por encima de lo de antes',
  },
  {
    n: 12,
    key: 'envion',
    points: 3_500,
    name: 'Envión',
    category: 'profundidad',
    unlocks: 'Tus hábitos se empujan unos a otros',
  },
  {
    n: 13,
    key: 'alcance',
    points: 4_000,
    name: 'Alcance',
    category: 'personalizacion',
    unlocks: 'Llegas a cosas que antes no alcanzabas',
  },
  {
    n: 14,
    key: 'escalon',
    points: 4_500,
    name: 'Escalón',
    category: 'profundidad',
    unlocks: 'Un escalón más alto que se queda',
  },
  {
    n: 15,
    key: 'ascenso',
    points: 5_000,
    name: 'Ascenso',
    category: 'personalizacion',
    unlocks: 'Cinco mil puntos: subes de verdad',
  },
  {
    n: 16,
    key: 'peldano',
    points: 5_500,
    name: 'Peldaño',
    category: 'profundidad',
    unlocks: 'Cada día suma un peldaño',
  },
  {
    n: 17,
    key: 'estribo',
    points: 6_000,
    name: 'Estribo',
    category: 'personalizacion',
    unlocks: 'Te apoyas en lo que ya construiste',
  },
  {
    n: 18,
    key: 'rellano',
    points: 6_500,
    name: 'Rellano',
    category: 'reconocimiento',
    unlocks: 'Un buen sitio para mirar lo recorrido',
  },
  {
    n: 19,
    key: 'altura',
    points: 7_000,
    name: 'Altura',
    category: 'personalizacion',
    unlocks: 'Ya se ve el camino desde arriba',
  },
  {
    n: 20,
    key: 'mirador',
    points: 7_500,
    name: 'Mirador',
    category: 'profundidad',
    unlocks: 'Desde aquí se ve todo lo que cambiaste',
  },
  {
    n: 21,
    key: 'atalaya',
    points: 8_000,
    name: 'Atalaya',
    category: 'personalizacion',
    unlocks: 'Ocho mil puntos de constancia',
  },
  {
    n: 22,
    key: 'elevacion',
    points: 8_500,
    name: 'Elevación',
    category: 'profundidad',
    unlocks: 'Lo que eras al empezar queda muy abajo',
  },
  {
    n: 23,
    key: 'amplitud',
    points: 9_000,
    name: 'Amplitud',
    category: 'personalizacion',
    unlocks: 'Tu progreso ya abarca toda tu vida',
  },
  {
    n: 24,
    key: 'remonte',
    points: 9_500,
    name: 'Remonte',
    category: 'profundidad',
    unlocks: 'La última subida antes de la cima',
  },
  {
    n: 25,
    key: 'planeo',
    points: 10_000,
    name: 'Planeo',
    category: 'reconocimiento',
    unlocks: 'Cierras el tramo Salto: diez mil puntos',
  },
];

/** Dónde termina el catálogo y empieza la pieza de horizonte. */
export const HORIZON_FROM = 10_000;

// ---------------------------------------------------------------------------
// TEMPORADAS Y LIGAS — otro reloj, en paralelo
// ---------------------------------------------------------------------------

export type Liga = {
  readonly key: string;
  readonly name: string;
  readonly from: number;
  /** Lo que significa al día, para explicarlo sin que parezca arbitrario. */
  readonly perDay: number;
  /** Llenado del sello, 0 a 1. La divisa de liga es un nivel de agua. */
  readonly fill: number;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA DECISIÓN QUE DEFINE LAS TEMPORADAS: **al cerrar no se resta nada.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Una temporada dura tres meses naturales y mide los puntos ganados DENTRO de
 * ella. Al terminar **sella la liga alcanzada y la archiva**. El camino, el
 * nivel, los hitos y las rachas siguen exactamente donde estaban.
 *
 * El reseteo de trofeos tipo Brawl Stars está prohibido aquí: decirle a alguien
 * que su progreso de salud se ha reiniciado porque acabó el trimestre es falso y
 * es hostil. Y **repetir Firme cuatro trimestres seguidos es el logro**, no el
 * fracaso: es el producto funcionando, y la sala de trofeos lo enseña como
 * historial de constancia, no como marcador.
 *
 * Prohibidas además la cuenta atrás de cierre —produce atracón— y cualquier
 * clasificación contra otras personas.
 */
export const LIGAS: readonly Liga[] = [
  { key: 'tenue', name: 'Tenue', from: 0, perDay: 0, fill: 0.12 },
  { key: 'regular', name: 'Regular', from: 900, perDay: 10, fill: 0.3 },
  { key: 'firme', name: 'Firme', from: 2_250, perDay: 25, fill: 0.5 },
  { key: 'plena', name: 'Plena', from: 4_800, perDay: 53, fill: 0.72 },
  { key: 'colmada', name: 'Colmada', from: 7_200, perDay: 80, fill: 1 },
];

/**
 * Firme es la tercera de cinco, media tabla, y es donde cae un uso constante y
 * honesto: 25 – 40 puntos al día son 2.250 – 3.600 en el trimestre. Plena exige
 * foco casi a diario. Colmada queda muy por debajo del máximo teórico (13.500)
 * justamente para que no sea una meta de atracón.
 */
const FALLBACK_LIGA: Liga = { key: 'tenue', name: 'Tenue', from: 0, perDay: 0, fill: 0.12 };

export function ligaForPoints(points: number): Liga {
  let current: Liga = LIGAS[0] ?? FALLBACK_LIGA;
  for (const liga of LIGAS) if (points >= liga.from) current = liga;
  return current;
}

export function nextLiga(points: number): Liga | null {
  return LIGAS.find((liga) => liga.from > points) ?? null;
}

/** `2026-Q3`. Trimestres naturales: todo el mundo cierra el mismo día. */
export function seasonKeyFor(localDate: string): string {
  const year = localDate.slice(0, 4);
  const month = Number(localDate.slice(5, 7));
  return `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
}

export function seasonRange(seasonKey: string): { from: string; to: string; label: string } {
  const [yearPart, quarterPart] = seasonKey.split('-Q');
  const year = Number(yearPart);
  const quarter = Number(quarterPart);
  const firstMonth = (quarter - 1) * 3 + 1;
  const lastMonth = firstMonth + 2;
  const lastDay = new Date(Date.UTC(year, lastMonth, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');

  const MONTHS = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];

  return {
    from: `${year}-${pad(firstMonth)}-01`,
    to: `${year}-${pad(lastMonth)}-${pad(lastDay)}`,
    label: `${MONTHS[firstMonth - 1]} – ${MONTHS[lastMonth - 1]} ${year}`,
  };
}
